import { useEffect, useRef } from 'react';
import { Button, Checkbox, Form, Input, Space, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import type { FormInstance } from 'antd';
import type { CreateAllyAdminState, CreateAllyFormState } from '../utils/fiscal';
import { ALLY_DUPLICATE_MESSAGES } from '../utils/allyErrors';

/**
 * Copy del bloque: la DECISIÓN (registrar ahora o después) y su consecuencia real
 * (los links se envían a mano, no hay correo). Sin jerga interna: el estado `PENDING`
 * del invitado no le cambia nada al operador y lo explica el modal del alta.
 */
const ADMINS_HELP =
  'Si el aliado ya tiene definidos sus administradores, se recomienda registrarlos ahora. A cada uno se le genera un link de invitación que hay que enviarle (no hay envío por correo). Si todavía no están confirmados, se pueden registrar después.';

interface AllyAdminsFieldsProps {
  form: FormInstance<CreateAllyFormState>;
  /** Emails tomados detectados por el chequeo por paso (TG-118); marca esas filas. */
  takenEmails?: ReadonlySet<string>;
}

/**
 * ¿La fila 0 es el espejo que escribimos (y no una fila cargada a mano)? Se compara por
 * valor contra el último titular reflejado: si el usuario editó o borró el espejo, ya no
 * coincide y lo tratamos como fila propia (y se conserva).
 */
function isMirrorRow(row: CreateAllyAdminState | undefined, mirror: CreateAllyAdminState | null) {
  return (
    mirror !== null &&
    row !== undefined &&
    (row.name ?? '') === (mirror.name ?? '') &&
    (row.email ?? '') === (mirror.email ?? '')
  );
}

/**
 * Bloque de administradores del alta de aliado (`Form.List`, bloque opcional).
 *
 * Cada entrada exige nombre y email. Para persona natural, el checkbox "el titular es el
 * administrador" mantiene la primera fila espejada a los datos del titular para no
 * tipear dos veces (caso común del franquiciado).
 */
export function AllyAdminsFields({ form, takenEmails }: AllyAdminsFieldsProps) {
  const personType = Form.useWatch('personType', form) ?? 'NATURAL';
  const ownerIsAdmin = Form.useWatch('ownerIsAdmin', form);
  const ownerFirstName = Form.useWatch('firstName', form);
  const ownerLastName = Form.useWatch('lastName', form);
  const ownerEmail = Form.useWatch('email', form);
  // El titular natural ahora se carga como nombres + apellidos: el admin espejado los une.
  const ownerName = [ownerFirstName, ownerLastName].filter(Boolean).join(' ').trim();

  // Revalida las filas cuando el chequeo por paso detecta emails tomados: el error sale
  // por el validador de la regla del email (y se limpia al corregirlo). `setFields` no
  // pinta errores en campos dentro de un `Form.List`.
  useEffect(() => {
    if (takenEmails && takenEmails.size > 0) {
      form.validateFields().catch(() => undefined);
    }
  }, [takenEmails, form]);

  // Última fila espejo que escribimos: permite reconocerla al desmarcar y no borrar por
  // error una fila que el usuario agregó a mano (que sí hay que conservar).
  const mirroredTitularRef = useRef<CreateAllyAdminState | null>(null);

  // Titular como administrador: la primera fila sigue a los datos del titular mientras el
  // checkbox esté marcado. Al desmarcar (o dejar de ser persona natural) se saca el espejo
  // y se conservan las filas que el usuario agregó a mano. Las filas agregadas después no
  // se tocan.
  useEffect(() => {
    const admins = (form.getFieldValue('admins') as CreateAllyAdminState[] | undefined) ?? [];
    const shouldMirror = personType === 'NATURAL' && ownerIsAdmin === true;

    if (!shouldMirror) {
      // Sólo se borra si la fila 0 sigue siendo el espejo: si el usuario la editó, pasó a
      // ser suya y se mantiene.
      if (isMirrorRow(admins[0], mirroredTitularRef.current)) {
        form.setFieldValue('admins', admins.slice(1));
      }
      mirroredTitularRef.current = null;
      return;
    }

    const titular: CreateAllyAdminState = { name: ownerName, email: ownerEmail ?? '' };
    // Re-sincroniza el espejo si sigue en la fila 0; si el usuario lo borró, antepone el
    // titular sin pisar las filas cargadas a mano.
    const rest = isMirrorRow(admins[0], mirroredTitularRef.current) ? admins.slice(1) : admins;
    form.setFieldValue('admins', [titular, ...rest]);
    mirroredTitularRef.current = titular;
  }, [personType, ownerIsAdmin, ownerName, ownerEmail, form]);

  return (
    <section>
      <Typography.Title level={5} style={{ marginTop: 0 }}>
        Administradores (opcional)
      </Typography.Title>
      <Typography.Paragraph type="secondary">{ADMINS_HELP}</Typography.Paragraph>

      {personType === 'NATURAL' && (
        <Form.Item name="ownerIsAdmin" valuePropName="checked" preserve={false}>
          <Checkbox>El titular es el administrador</Checkbox>
        </Form.Item>
      )}

      <AllyAdminRows takenEmails={takenEmails} />
    </section>
  );
}

/**
 * Filas de administrador (nombre + email) sobre `Form.List name="admins"`. Extraído del
 * bloque del alta para reusarlo en el modal de "agregar administradores" del detalle (F11)
 * sin duplicar la fila ni sus validaciones.
 */
export function AllyAdminRows({ takenEmails }: { takenEmails?: ReadonlySet<string> } = {}) {
  return (
    <Form.List name="admins">
      {(fields, { add, remove }) => (
        <>
          {fields.map((field, index) => (
            <Space key={field.key} align="center" style={{ display: 'flex', marginBottom: 8 }}>
              <Form.Item
                label={index === 0 ? 'Nombre' : undefined}
                name={[field.name, 'name']}
                rules={[{ required: true, message: 'Ingresa el nombre del administrador.' }]}
                style={{ marginBottom: 0 }}
              >
                <Input
                  autoComplete="off"
                  placeholder="Introduzca el nombre del administrador"
                  aria-label={`Nombre del administrador ${index + 1}`}
                />
              </Form.Item>
              <Form.Item
                label={index === 0 ? 'Email' : undefined}
                name={[field.name, 'email']}
                rules={[
                  { required: true, message: 'Ingresa el email del administrador.' },
                  { type: 'email', message: 'Ingresa un email válido.' },
                  {
                    // Chequeo consultivo por paso (TG-118): marca la fila si el email ya
                    // está tomado. Se limpia solo al editar el email (revalidación nativa).
                    validator: (_, value: string | undefined) => {
                      const email = value?.trim() ?? '';
                      return email && takenEmails?.has(email)
                        ? Promise.reject(new Error(ALLY_DUPLICATE_MESSAGES.adminEmail(email)))
                        : Promise.resolve();
                    },
                  },
                ]}
                style={{ marginBottom: 0 }}
              >
                <Input
                  autoComplete="off"
                  placeholder="Introduzca el email del administrador"
                  aria-label={`Email del administrador ${index + 1}`}
                />
              </Form.Item>
              {/* El botón va dentro de un `Form.Item` con el mismo label (solo en la fila 0)
                  para centrarlo con el CAMPO, no con la fila entera (label + control). Con
                  `align="center"` los ítems de una fila quedan a la misma altura y el botón
                  centra contra los controles, no contra el texto del label. */}
              <Form.Item label={index === 0 ? ' ' : undefined} style={{ marginBottom: 0 }}>
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  aria-label={`Quitar administrador ${index + 1}`}
                  onClick={() => remove(field.name)}
                />
              </Form.Item>
            </Space>
          ))}

          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({})} block>
              Agregar administrador
            </Button>
          </Form.Item>
        </>
      )}
    </Form.List>
  );
}
